import React from 'react';
import { useTranslation } from '../../i18n';
import { Todo } from '../../services/goalService';
import Icon, { IconName } from '../Icon';
import './TodoItem.css';
import './TodoRow.css';
import { clickableProps } from '../../utils/a11y';
import PriorityIcon from '../PriorityIcon/PriorityIcon';
import TagChip from '../TagChip/TagChip';

const MAX_VISIBLE_TAGS = 3;

interface TodoItemProps {
  todo: Todo;
  onToggle: (todoId: string) => void;
  /** Kept for callers; deleting happens on the todo page */
  onDelete?: (todoId: string) => void;
  onViewDetails: (todo: Todo) => void;
  startTodo?: (todoId: string) => void;
  stopTodo?: (todoId: string) => void;
  toggleTimely?: (todoId: string) => void;
  onMove?: (todo: Todo) => void;
  className?: string;
  dragHandle?: React.ReactNode;
}

interface RowActionProps {
  icon: IconName;
  label: string;
  onClick: () => void;
  className?: string;
  /** Set for an on/off action (Timely), so screen readers say its state */
  pressed?: boolean;
}

/** A 44px icon button at the end of a row; the click does not open the todo. */
const RowAction: React.FC<RowActionProps> = ({ icon, label, onClick, className = '', pressed }) => (
  <button
    type="button"
    className={`todo-row__action ${className}`.trim()}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    aria-label={label}
    title={label}
    {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
  >
    <Icon name={icon} />
  </button>
);

/**
 * One todo row on the sub goal page (T-104, same as native T-091): the drag handle for open
 * todos, a round complete / undo check, the priority by shape and color, title and tags,
 * then Timely, start / pause and move as icons, all on one line. The row opens the todo.
 */
const TodoItem: React.FC<TodoItemProps> = ({
  todo,
  onToggle,
  onViewDetails,
  startTodo,
  stopTodo,
  toggleTimely,
  onMove,
  className = '',
  dragHandle,
}) => {
  const { t } = useTranslation();
  const done = todo.completed;
  const tags = todo.tags ?? [];
  const checkLabel = done ? t('todos.item.tooltips.markIncomplete') : t('todos.item.tooltips.markComplete');
  const timelyLabel = todo.isTimely ? t('todos.item.buttons.removeFromTimely') : t('todos.item.buttons.addToTimely');

  return (
    <div
      className={`todo-row ${done ? 'todo-row--done' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
      {...clickableProps(() => onViewDetails(todo), 'link')}
      title={t('todos.item.tooltips.viewDetails')}
    >
      {dragHandle && !done && (
        <div className="todo-row__drag" onClick={(e) => e.stopPropagation()}>
          {dragHandle}
        </div>
      )}

      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        className={`todo-row__check ${done ? 'is-checked' : ''}`.trim()}
        onClick={(e) => { e.stopPropagation(); onToggle(todo.id); }}
        aria-label={checkLabel}
        title={checkLabel}
      >
        <span className="todo-row__check-circle" aria-hidden="true">
          {done && <Icon name="check" />}
        </span>
      </button>

      {!done && <PriorityIcon priority={todo.priority} className="todo-row__priority" />}

      <div className="todo-row__text">
        <span className="todo-row__title">{todo.title}</span>
        {tags.length > 0 && (
          <span className="todo-row__tags">
            {tags.slice(0, MAX_VISIBLE_TAGS).map((tag) => <TagChip key={tag.id} tag={tag} />)}
            {tags.length > MAX_VISIBLE_TAGS && (
              <span className="todo-row__more">{t('tags.more', { count: tags.length - MAX_VISIBLE_TAGS })}</span>
            )}
          </span>
        )}
      </div>

      {!done && (
        <div className="todo-row__actions">
          {toggleTimely && (
            <RowAction
              icon="clock"
              label={timelyLabel}
              pressed={Boolean(todo.isTimely)}
              className={todo.isTimely ? 'is-timely' : ''}
              onClick={() => toggleTimely(todo.id)}
            />
          )}
          {!todo.isStarted && startTodo && (
            <RowAction icon="play" label={t('todos.item.tooltips.start')} onClick={() => startTodo(todo.id)} />
          )}
          {todo.isStarted && stopTodo && (
            <RowAction icon="pause" label={t('todos.item.tooltips.stop')} className="is-running" onClick={() => stopTodo(todo.id)} />
          )}
          {onMove && (
            <RowAction icon="move" label={t('moveTodo.title')} className="todo-row__action--move" onClick={() => onMove(todo)} />
          )}
        </div>
      )}
    </div>
  );
};

export default TodoItem;
